import { X, Loader2, Send, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import type { Customer, Order, Template } from "./types";

interface SendMessageModalProps {
  customer: Customer;
  templates: Template[];
  selectedTemplateType: "email" | "pdf";
  selectedOrder: Order | null;
  isPendingSend: boolean;
  onClose: () => void;
  onSelectOrder: (order: Order | null) => void;
  onSend: (data: { templateId: string; orderId?: string }) => void;
}

export default function SendMessageModal({
  customer,
  templates,
  selectedTemplateType,
  selectedOrder,
  isPendingSend,
  onClose,
  onSelectOrder,
  onSend,
}: SendMessageModalProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedTemplateType === "email"
                ? t("crm.sendModal.titleEmail")
                : t("crm.sendModal.titleDocument")}
            </h3>
            <button
              title={t("common.actions.close")}
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Recipient info */}
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("crm.sendModal.recipient")}
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {customer.firstName} {customer.lastName}
              </p>
              <p className="text-sm text-gray-500">{customer.email}</p>
            </div>

            {/* Order selection (if applicable) */}
            {customer.orders && customer.orders.length > 0 && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("crm.sendModal.orderOptional")}
                </label>
                <select
                  value={selectedOrder?.id || ""}
                  onChange={(e) => {
                    const order = customer.orders?.find((o) => o.id === e.target.value);
                    onSelectOrder(order || null);
                  }}
                  className="input"
                >
                  <option value="">{t("crm.sendModal.withoutOrder")}</option>
                  {customer.orders.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.orderNumber} - {order.trip?.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Template selection */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.sendModal.template")}
              </label>
              <select id="template-select" className="input">
                <option value="">{t("crm.sendModal.selectTemplate")}</option>
                {templates
                  .filter((t) => t.type === selectedTemplateType)
                  .map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Preview hint */}
            <p className="text-xs text-gray-500">{t("crm.sendModal.variablesHint")}</p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button onClick={onClose} className="btn-ghost">
              {t("common.actions.cancel")}
            </button>
            <button
              onClick={() => {
                const templateSelect = document.getElementById(
                  "template-select",
                ) as HTMLSelectElement;
                const templateId = templateSelect?.value;
                if (!templateId) {
                  toast.error(t("crm.sendModal.selectTemplate"));
                  return;
                }
                onSend({
                  templateId,
                  orderId: selectedOrder?.id,
                });
              }}
              disabled={isPendingSend}
              className="btn-primary"
            >
              {isPendingSend ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : selectedTemplateType === "email" ? (
                <Send className="mr-2 h-4 w-4" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {selectedTemplateType === "email"
                ? t("common.actions.send")
                : t("crm.sendModal.generateAndSend")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
